/**
 * D-4 支付 —— `/api/payments`（契约 §7 D-4，2026-09-09 双侧签字解冻）。
 *
 * ★ **这个端点不是「建单」。** 契约禁的是 `/api/orders` 加 POST 建单，因为建单要过
 *   金额确认闸门；取支付参数不是建单 —— 金额 = 订单金额 = 建单时已确认过的金额，
 *   用户在这一步**没有任何选择余地**。为把敞口封死，后端有三条硬限制，其中两条
 *   直接决定本文件的形状：
 *
 *   1. **请求体不接受任何金额字段**。一旦请求能带金额，闸门当场失效 ——
 *      所以 {@link createPaymentRequestSchema} 是 `strictObject` 而不是 loose：
 *      这是全包唯一一处用 strict 的地方，理由见那里的注释。
 *   2. **只接受已存在、且属于当前用户的订单**。不存在与不属于**回同一个码**
 *      （`COMMON.NOT_FOUND`）—— 区分了就是给攻击者一个订单号探测器。
 *   3. **幂等**：同一订单重复 POST 返回同一个未过期会话；过期才换新的。
 *      ⇒ 前端**不需要**判断「当前有没有可用会话」，用户想付款时直接 POST 就是了。
 *
 * ⚠⚠ **没有支付成功的推送。** 渠道回调是异步打到我方服务端的，前端不参与。
 *   发起支付后**轮询 `GET /api/orders/{orderNo}`**，`state` 变 `ORDER_PAID` 即到账。
 *   见 `apps/app/src/components/me/order-pay-panel.tsx`。
 */
import { z } from 'zod'

/**
 * 收款渠道。⚠ **只有这两个**（ADR-002，2026-09-03 拍板）——
 * 对公转账是线下兜底、不走系统，别加第三个值。
 *
 * ⚠⚠ **`ALIPAY` 今天一定会失败**：后端 `channel/alipay/` 下只有一个 README，
 *   `PaymentChannel.ALIPAY` 的注释写着「尚未接入：商户资质是长周期外部依赖」。
 *   未启用的渠道回 `COMMON.INVALID_ARGUMENT`，**不会静默退回微信**
 *   （那会让用户以为选了支付宝却拉起微信）。
 *   ⇒ 界面上**不要把支付宝列成可选项**，等后端 `PaymentChannelRegistry`
 *     的启动日志里真出现 `ALIPAY` 再放开。这里声明它只是为了把契约映射全。
 */
export const PAYMENT_CHANNELS = ['WECHAT', 'ALIPAY'] as const
export type PaymentChannel = (typeof PAYMENT_CHANNELS)[number]

/**
 * 发起支付的请求体。
 *
 * ⚠⚠ **这是全包唯一一个 `strictObject`。** 别处一律 `looseObject`（放行后端新增字段，
 *   那是「新增不需要前端发版」的对价）。**这里反过来**：多一个字段的风险不是渲染错，
 *   而是**有人往请求里塞 `amount`**，而金额闸门守的就是这件事。strict 让那种改动
 *   在类型检查与单测里当场红掉，而不是等到线上多收了钱才发现。
 *
 * ⚠ `channel` 由前端指定，但**认不认由后端 `PaymentChannelRegistry` 说**。
 */
export const createPaymentRequestSchema = z.strictObject({
  orderNo: z.string().min(1).max(64),
  channel: z.enum(PAYMENT_CHANNELS),
})
export type CreatePaymentRequest = z.infer<typeof createPaymentRequestSchema>

/**
 * 微信 APP 支付的拉起参数 —— 直接喂微信 SDK 的七个。
 *
 * ⚠⚠ **字段名就是线上协议，改一个字母就是「拉起没反应」，而且不报错。**
 *   名字由后端 `channel/wechat/WechatAppPayParams` 定，照微信文档拼写。
 * ⚠ `package` 固定是 `Sign=WXPay`；`sign` 是签名**结果**，不是密钥 ——
 *   这组参数**不含任何密钥**，可以安全下发给客户端。
 * ⚠ `timeStamp` 是**字符串**（微信要求），别当数字用。
 *
 * ⚠⚠ **这是 APP 支付**（后端 `WechatPayClient.APP_PAY_PATH = /v3/pay/transactions/app`）。
 *   浏览器里这七个参数**无处可用** —— 要有微信原生 SDK 才拉得起来。
 *   Web 端的处置见 `apps/app/src/lib/native-pay.ts`。
 */
export const wechatAppPayParamsSchema = z.looseObject({
  appId: z.string().min(1),
  partnerId: z.string().min(1),
  prepayId: z.string().min(1),
  package: z.string().min(1),
  nonceStr: z.string().min(1),
  timeStamp: z.string().min(1),
  sign: z.string().min(1),
})
export type WechatAppPayParams = z.infer<typeof wechatAppPayParamsSchema>

/** 支付宝 APP 支付：服务端签好的完整业务串，整个交给支付宝 SDK。 */
export const alipayParamsSchema = z.looseObject({
  orderStr: z.string().min(1),
})
export type AlipayParams = z.infer<typeof alipayParamsSchema>

/**
 * `PaymentInvoke` —— 一次支付会话。
 *
 * ⚠⚠ **`params` 的形状随渠道而变**（2026-09-10 破坏性变更，iOS 已同步）。
 *   此前是写死的 `wechatApp` 字段；改成 `params` 是因为支付宝 APP 支付返回的是
 *   **一个字符串**（`orderStr`），与微信那七个字段的对象**没有任何共同结构**。
 *
 * ⚠⚠ **不要给 `params` 定义「两个渠道字段的并集」结构体** —— 契约原文点名禁止：
 *   那样每加一个渠道就要改一次公共类型，且所有字段都得可选、谁都不知道哪些真有值。
 *   **按 `channel` 分支去挑**，出口就是 {@link wechatAppPayParamsOf} /
 *   {@link alipayParamsOf}，别在别处再 as 一次。
 *
 * ⚠ `expiresAt` 是 **epoch 毫秒**。到点后重新 POST 换新会话即可 ——
 *   页面内的倒计时用它，**不要**据此去问后端「会话还在不在」（没有那个接口，也不需要）。
 *
 * ⚠⚠ **`paymentNo` 不保证恒定。** 幂等的「未过期」带一个安全余量（当前 10 分钟）：
 *   剩余有效期不足余量时按过期处理、换新会话。理由是用户点开微信、输密码、等银行
 *   返回要花时间，剩十几秒时复用会让会话在**付款流程中间**失效。
 *   ⇒ **别缓存 `paymentNo`、别拿它做幂等键**，以每次应答为准。
 */
export const paymentInvokeSchema = z.looseObject({
  /** 我方支付单号。⚠ 见上：不保证恒定，只用于展示与排查。 */
  paymentNo: z.string().min(1),
  /** ⚠ 用 `z.string()` 而不是 enum：后端将来加渠道，前端不该整页解析失败。 */
  channel: z.string().min(1),
  /** 本次会话失效时刻，**epoch 毫秒**。 */
  expiresAt: z.number().nullable().default(null),
  /** ⚠ 形状随渠道而变，别在这里收窄 —— 用下面两个取用函数。 */
  params: z.looseObject({}).nullable().default(null),
})
export type PaymentInvoke = z.infer<typeof paymentInvokeSchema>

/**
 * 取微信拉起参数。**渠道对不上、或字段不全就回 `null`** —— 调用方据此降级，
 * 绝不半信半疑地把缺字段的参数递给 SDK（那是「拉起没反应且不报错」的来源）。
 */
export function wechatAppPayParamsOf(invoke: PaymentInvoke): WechatAppPayParams | null {
  if (invoke.channel !== 'WECHAT') return null
  const parsed = wechatAppPayParamsSchema.safeParse(invoke.params)
  return parsed.success ? parsed.data : null
}

/** 取支付宝拉起串。同上：渠道对不上或缺 `orderStr` 就回 `null`。 */
export function alipayParamsOf(invoke: PaymentInvoke): AlipayParams | null {
  if (invoke.channel !== 'ALIPAY') return null
  const parsed = alipayParamsSchema.safeParse(invoke.params)
  return parsed.success ? parsed.data : null
}

/**
 * ⚠⚠ **可支付的订单状态只有一个**（契约 §7 D-4，2026-09-10 收窄，此前还含 `ORDER_CREATED`）。
 *
 * 两条各自独立成立的理由，任一条都够：
 *
 * 1. **对机票本身就是错的**：`ORDER_CREATED` = 我方受理了、**供应商还不知道这单存在**；
 *    `ORDER_PENDING_PAYMENT` = 供应商已占座（PNR 已出）。在 CREATED 收钱 =
 *    为一个**还没有人给我们留座**的行程收钱。
 * 2. 🔴 **它是「给供应商直收的域重复收款」的入口**：打车域是 `SettlementMode.SUPPLIER`
 *    （腾讯出行行程结束后**直接向用户收款**），而**打车订单的初态正是 `ORDER_CREATED`**。
 *    放它进来的后果是：钱真扣了 → 回调 → 状态机这时才拒绝 → 订单没动 → 腾讯还会再收一次。
 *
 * ★ **前端因此不必自己判断「这单是不是供应商直收」**：`ORDER_PENDING_PAYMENT`
 *   本身就在后端 `OrderStateMachine.PAYMENT_STATES` 里，而那道闸门**禁止 SUPPLIER
 *   口径的订单进入其中任何一个** ⇒ 打车订单永远不可能落到本集合里，**由构造保证**。
 *   ⚠ 这条很要紧：`OrderDetailView` **没有 `domain` 字段**，详情页压根看不出这是不是
 *   打车单 —— 能安全地只按状态开支付入口，靠的就是上面这个构造性保证，不是巧合。
 *
 * ⚠ **不要把 `ORDER_CREATED` 加回来**。后端有 `PaymentControllerPayableStatesTest`
 *   拦这个改动，前端这边由 `payments.test.ts` 拦。
 */
export const PAYABLE_ORDER_STATES = ['ORDER_PENDING_PAYMENT'] as const

/** 这个状态能不能发起支付。⚠ 判据只有一个状态，理由见 {@link PAYABLE_ORDER_STATES}。 */
export function isPayableOrderState(state: string): boolean {
  return (PAYABLE_ORDER_STATES as readonly string[]).includes(state)
}

/**
 * ⚠⚠ **下单成功但状态还没回填到 `ORDER_PENDING_PAYMENT` 时点支付会回 `COMMON.CONFLICT`。**
 *
 * 契约原文给前端的处置：「**轮询到状态变了再给支付入口，不要把 `CONFLICT` 当成
 * 错误弹给用户**」。这是一个竞态，不是用户做错了什么 —— 弹一句红色的「冲突」
 * 只会让人以为订单坏了。
 *
 * ⇒ 界面收到它要说「订单状态刚刚更新，请稍候重试」并**主动刷一次订单**。
 */
export const PAYMENT_RACE_CODE = 'COMMON.CONFLICT'
